import React, { useMemo } from "react";
import { useWidgetFiltersController } from "./useWidgetFiltersController";
import { ITableWidget } from "../table";
import { Widget } from "@/modules";
import { DimensionFilter } from "./DimensionFilter";
import { ProductFilter } from "./ProductFilter";
import { PeriodFilter } from "./PeriodFilter";
import { MultiDimensionFilter } from "./MultiDimensionFilter";
import { hasMultiFiltersEnabled } from "@/constants/widget";
import { DateRangeSelector } from "./DateRangeSelector";
import { ServiceFilters } from "./ServiceFilters";
import { DataFilter, matchesDataFilter } from "@/modules/widgets/types/dataFilters";

export interface IWidgetFiltersProps {
    widgetFiltersController: ReturnType<typeof useWidgetFiltersController>;
    filter: ITableWidget['data']['metadata']['filter'];
    filters?: ITableWidget['data']['metadata']['filters'];
    widget: Widget;
    widgetData: any;
}
export const WidgetFilters: React.FC<IWidgetFiltersProps> = ({ widgetFiltersController, filter, filters, widget, widgetData }) => {
    const items = useMemo(
        () => (filter?.items || []).map((item: any) => ({ id: item, text: item, data: item })),
        [filter?.items],
    );
    // A "campaign" segment/level breakdown can report its dimension showName as "Segment"; relabel it
    // to "Campaign" so the runtime control reads "Filter by Campaign" (matches the Advanced Settings toggle).
    const isCampaignSegment =
        (widget as any)?.breakdownBy === "segment" &&
        String((widget as any)?.service?.parameters?.segment ?? "").toLowerCase() === "campaign";
    const isCampaignLevel =
        (widget as any)?.breakdownBy === "level" &&
        String((widget as any)?.service?.parameters?.level ?? "").toLowerCase() === "campaign";
    const filterLabel = isCampaignSegment || isCampaignLevel ? "Campaign" : filter?.dimension?.showName;
    const dimensionMetricName = filter?.dimension?.metricName;
    // Pre-select the campaigns the configured data-filters would match. Campaign filtering is
    // front-end-only, so we evaluate each filter's operator (contains/equals/startsWith/…) against
    // the available items — mirroring the backend — instead of an exact value match. A `contains
    // "Model"` filter therefore pre-selects every "Model…" campaign, leaving the rest available to add.
    const dimensionFilters = useMemo(() => {
        if (!dimensionMetricName) return [] as DataFilter[];
        const dataFilters = ((widget?.metadata as any)?.["data-filters"] ?? []) as DataFilter[];
        return dataFilters.filter((f) => f?.enabled !== false && f?.operator && f.field === dimensionMetricName);
    }, [widget, dimensionMetricName]);
    // A configured dimension filter is authoritative even when it matches zero campaigns — that means
    // "show none", not "fall back to all". Pass the flag so DimensionFilter can tell the two apart.
    const hasConfiguredFilter = dimensionFilters.length > 0;
    const initialSelectedItems = useMemo(() => {
        if (!dimensionMetricName || !dimensionFilters.length) return [] as typeof items;
        return items.filter((it) => dimensionFilters.every((f) => matchesDataFilter({ [dimensionMetricName]: it.text }, f)));
    }, [items, dimensionFilters, dimensionMetricName]);
    // Signature of the configured campaign filter(s). Changes when the filter is edited, prompting
    // DimensionFilter to re-seed the campaign selection to the new match (LAD-452).
    const filterSignature = useMemo(
        () => JSON.stringify(dimensionFilters.map((f) => [f.field, f.operator, f.value, f.enabled ?? true])),
        [dimensionFilters],
    );
    const hasRowsToFilter = (widgetData?.data?.rows ?? []).length > 0;

    // Same pre-selection for the multi-filter path (LAD-453): an AI widget can carry a
    // "multi-filters" dropdown AND a data-filter on the same dimension (e.g. "show the campaign
    // dropdown" + "only Branded campaigns"). The backend skips that data-filter server-side and
    // relies on the dropdown to apply it, so seed each dropdown to the values its filters match.
    const multiFilterSeeds = useMemo(() => {
        const dataFilters = ((widget?.metadata as any)?.["data-filters"] ?? []) as DataFilter[];
        const seeds: Record<string, { matchingValues: string[]; signature: string }> = {};
        for (const filterConfig of filters ?? []) {
            const metricFilters = dataFilters.filter(
                (f) => f?.enabled !== false && f?.operator && f.field === filterConfig.metric,
            );
            if (!metricFilters.length) continue;
            seeds[filterConfig.metric] = {
                matchingValues: (filterConfig.items ?? []).filter((item) =>
                    metricFilters.every((f) => matchesDataFilter({ [filterConfig.metric]: item }, f)),
                ),
                signature: JSON.stringify(metricFilters.map((f) => [f.field, f.operator, f.value, f.enabled ?? true])),
            };
        }
        return seeds;
    }, [filters, widget]);

    const hasMultiFilters = filters && Array.isArray(filters) && filters.length > 0;

    const multiFiltersEnabled = hasMultiFiltersEnabled(widget, widgetData);

    return (
        <>
            <ProductFilter widget={widget} controller={widgetFiltersController} widgetData={widgetData} />
            <ServiceFilters widget={widget} controller={widgetFiltersController} widgetData={widgetData} />
            {/* <PeriodFilter widget={widget} controller={widgetFiltersController} /> */}
            <DateRangeSelector widget={widget} controller={widgetFiltersController} />

            {hasMultiFilters && multiFiltersEnabled && (
                <MultiDimensionFilter filters={filters} controller={widgetFiltersController} seeds={multiFilterSeeds} />
            )}
            {!hasMultiFilters && filter?.enabled && hasRowsToFilter && widget.type === "table" ? (
                <DimensionFilter
                    items={items}
                    controller={widgetFiltersController}
                    filterCTA={filterLabel ? `Filter by ${filterLabel}` : 'Filter'}
                    initialSelectedItems={initialSelectedItems}
                    hasConfiguredFilter={hasConfiguredFilter}
                    filterSignature={filterSignature}
                />
            ) : null}
        </>
    );
};
